import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNotification, DeleteConfirmationModal, ConfirmationModal } from '../common';

const AutomationSettings = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [automationRules, setAutomationRules] = useState([]);
  const [assignmentGroups, setAssignmentGroups] = useState([]);
  const [newRule, setNewRule] = useState({
    name: '',
    description: '',
    trigger_event: 'lead_created', // Keep for backward compatibility
    triggers: ['lead_created'],    // New array to support multiple triggers
    action_type: 'update_lead',    // Keep for backward compatibility
    conditions: {},
    action_data: {  // Keep for backward compatibility
      fields: {}
    },
    actions: [     // New array to support multiple actions
      {
        action_type: 'update_lead',
        action_data: {
          fields: {}
        }
      }
    ],
    is_active: true
  });
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editRuleId, setEditRuleId] = useState(null);
  const [users, setUsers] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false); // State for close confirmation
  const [initialRuleState, setInitialRuleState] = useState(null); // State to store initial rule data for comparison
  const { showNotification } = useNotification();

  // State to store email templates and SMTP profiles
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [smtpProfiles, setSmtpProfiles] = useState([]);
  // State to store pipeline stages and courses
  const [pipelineStages, setPipelineStages] = useState([]);
  // State to store courses
  const [courses, setCourses] = useState([]);

  // Fetch automation rules, users, assignment groups, email templates, SMTP profiles, pipeline stages, and courses when component mounts
  useEffect(() => {
    fetchAutomationRules();
    fetchUsers();
    fetchAssignmentGroups();
    fetchEmailTemplates();
    fetchSmtpProfiles();
    fetchPipelineStages();
    fetchCourses();
  }, []);

  // Fetch automation rules from the API
  const fetchAutomationRules = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const response = await axios.get('/api/automation/rules', config);
      
      // Ensure all rules have the triggers array initialized
      const processedRules = response.data.map(rule => {
        // If rule doesn't have triggers array, create it from trigger_event
        if (!rule.triggers && rule.trigger_event) {
          return { ...rule, triggers: [rule.trigger_event] };
        }
        return rule;
      });
      
      setAutomationRules(processedRules);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching automation rules:', err);
      setError('Failed to load automation rules: ' + (err.response?.data?.message || err.message));
      setLoading(false);
    }
  };

  // Fetch users for assignment options
  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const response = await axios.get('/api/auth/users', config);
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };
  
  // Fetch assignment groups for round-robin assignment
  const fetchAssignmentGroups = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const response = await axios.get('/api/automation/assignment-groups', config);
      setAssignmentGroups(response.data);
    } catch (err) {
      console.error('Error fetching assignment groups:', err);
    }
  };
  
  // Fetch email templates for email actions
  const fetchEmailTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const response = await axios.get('/api/email/templates', config);
      setEmailTemplates(response.data);
    } catch (err) {
      console.error('Error fetching email templates:', err);
    }
  };
  
  // Fetch SMTP profiles for email sending
  const fetchSmtpProfiles = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      // Fetch SMTP profiles from the email settings API
      const response = await axios.get('/api/settings/email-profiles', config);
      setSmtpProfiles(response.data);
    } catch (err) {
      console.error('Error fetching SMTP profiles:', err);
      // If there's an error, we'll just use the default profile
    }
  };

  // Fetch pipeline stages for deal conditions
  const fetchPipelineStages = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      // Fetch pipeline stages from the deals API
      const response = await axios.get('/api/deals/stages/all', config);
      setPipelineStages(response.data);
    } catch (err) {
      console.error('Error fetching pipeline stages:', err);
      // If there's an error, we'll fall back to default stages
    }
  };
  
  // Fetch courses for deal conditions
  const fetchCourses = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      // Fetch active courses
      const response = await axios.get('/api/settings/courses/active', config);
      setCourses(response.data);
    } catch (err) {
      console.error('Error fetching courses:', err);
      // If there's an error, we'll fall back to an empty list
    }
  };

  // Helper function to check for unsaved changes
  const hasUnsavedChanges = useCallback(() => {
    if (!initialRuleState) return false; // No initial state to compare against
    // Compare stringified versions for deep comparison
    return JSON.stringify(initialRuleState) !== JSON.stringify(newRule);
  }, [initialRuleState, newRule]);

  // Function to reset state and close the modal
  const closeModalCleanup = useCallback(() => {
    setNewRule({
      name: '',
      description: '',
      trigger_event: 'lead_created', // Default trigger event
      action_type: 'update_lead',   // For backward compatibility
      conditions: {},
      action_data: {                // For backward compatibility
        fields: {}
      },
      actions: [                    // New array for multiple actions
        {
          action_type: 'update_lead',
          action_data: {
            fields: {}
          }
        }
      ],
      is_active: true
    });
    setInitialRuleState(null);
    setIsEditMode(false);
    setEditRuleId(null);
    setShowRuleModal(false);
    setShowCloseConfirm(false); // Ensure confirmation modal is also closed
  }, []); // No dependencies needed as it only uses setters

  // Function to handle attempts to close the modal
  const handleAttemptClose = useCallback(() => {
    if (hasUnsavedChanges()) {
      setShowCloseConfirm(true);
    } else {
      closeModalCleanup();
    }
  }, [hasUnsavedChanges, closeModalCleanup]);


  // Handle toggling rule active status
  const handleToggleRule = async (id) => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const response = await axios.patch(
        `/api/automation/rules/${id}/toggle`,
        {},
        config
      );
      
      // Update the rule in the local state
      setAutomationRules(automationRules.map(rule => 
        rule.id === id ? response.data : rule
      ));
      
      showNotification('Rule status updated successfully', 'success');
      setLoading(false);
    } catch (err) {
      console.error('Error toggling rule status:', err);
      showNotification('Failed to update rule status: ' + (err.response?.data?.message || err.message), 'error');
      setLoading(false);
    }
  };

  // Handle deleting a rule
  const handleDeleteRule = async (id) => {
    setRuleToDelete(id);
    setShowDeleteConfirm(true);
  };
  
  const confirmDeleteRule = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      await axios.delete(`/api/automation/rules/${ruleToDelete}`, config);
      
      // Remove the rule from local state
      setAutomationRules(automationRules.filter(rule => rule.id !== ruleToDelete));
      
      showNotification('Rule deleted successfully', 'success');
      setLoading(false);
      setShowDeleteConfirm(false);
    } catch (err) {
      console.error('Error deleting rule:', err);
      showNotification('Failed to delete rule: ' + (err.response?.data?.message || err.message), 'error');
      setLoading(false);
    }
  };

  // Handle form input changes for new rule
  const handleNewRuleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    console.log(`INPUT DEBUG: Change in field "${name}" with value "${value}"`);
    
    if (name.startsWith('condition_')) {
      // Handle condition fields
      const conditionField = name.replace('condition_', '');
      console.log(`CONDITION DEBUG: Setting condition field "${conditionField}" to "${value}"`);
      
      setNewRule(prev => {
        const newState = {
          ...prev,
          conditions: {
            ...prev.conditions,
            [conditionField]: value
          }
        };
        console.log(`CONDITION DEBUG: New state:`, JSON.stringify(newState.conditions, null, 2));
        return newState;
      });
    } else if (name.startsWith('action_')) {
      // Handle action fields (update_lead fields)
      const actionField = name.replace('action_', '');
      console.log(`ACTION DEBUG: Setting action field "${actionField}" to "${value}"`);
      
      // Special debugging for country field
      if (actionField === 'country') {
        console.log(`COUNTRY DEBUG: Setting country field to "${value}"`);
      }
      
      setNewRule(prev => {
        const newState = {
          ...prev,
          action_data: {
            ...prev.action_data,
            fields: {
              ...prev.action_data.fields,
              [actionField]: value
            }
          }
        };
        console.log(`ACTION DEBUG: New fields:`, JSON.stringify(newState.action_data.fields, null, 2));
        return newState;
      });
    } else if (name === "action_type") {
      // When action type changes, reset action_data appropriately for that type
      let newActionData = {};
      
      if (value === "update_lead") {
        newActionData = { fields: {} };
      } else if (value === "round_robin_assign") {
        newActionData = { group_name: "" };
      }
      
      setNewRule(prev => ({
        ...prev,
        action_type: value,
        action_data: newActionData
      }));
    } else {
      // Handle regular form fields
      setNewRule(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  // Handle select changes for country condition
  const handleCountryConditionChange = (e) => {
    const { value } = e.target;
    
    setNewRule(prev => {
      // Get a fresh copy of conditions
      const updatedConditions = { ...prev.conditions };
      
      // If value is empty, remove the country condition
      if (!value) {
        delete updatedConditions.country;
      } else {
        // Otherwise, set the country
        updatedConditions.country = value;
      }
      
      return {
        ...prev,
        conditions: updatedConditions
      };
    });
  };

  // Handle edit rule
  const handleEditRule = (rule) => {
    // Convert the rule's structure to match our form state
    const preparedRule = {
      name: rule.name,
      description: rule.description || '',
      trigger_event: rule.trigger_event,     // For backward compatibility
      
      // For triggers, ensure we always have an array with at least the trigger_event
      // If rule.triggers exists and is an array, use it; otherwise create a new array from trigger_event
      triggers: (rule.triggers && Array.isArray(rule.triggers) && rule.triggers.length > 0) 
                ? rule.triggers 
                : [rule.trigger_event],
                
      action_type: rule.action_type,         // For backward compatibility
      conditions: rule.conditions || {},
      action_data: rule.action_data || {},   // For backward compatibility
      is_active: rule.is_active
    };
    
    // If action_data doesn't have fields property (for update_lead) and it's needed
    if (rule.action_type === 'update_lead' && !preparedRule.action_data.fields) {
      preparedRule.action_data.fields = {};
    }
    
    // Handle the new 'actions' field - either from the rule or create it from legacy fields
    if (rule.actions && Array.isArray(rule.actions) && rule.actions.length > 0) {
      // New format with multiple actions
      preparedRule.actions = rule.actions;
    } else if (rule.action_type && rule.action_data) {
      // Convert legacy format to actions array
      preparedRule.actions = [
        {
          action_type: rule.action_type,
          action_data: rule.action_data
        }
      ];
    } else {
      // Default empty array with one action
      preparedRule.actions = [
        {
          action_type: 'update_lead',
          action_data: {
            fields: {}
          }
        }
      ];
    }
    
    setNewRule(preparedRule);
    setInitialRuleState(JSON.parse(JSON.stringify(preparedRule))); // Store initial state for comparison
    setIsEditMode(true);
    setEditRuleId(rule.id);
    setShowRuleModal(true);
  };

  // Submit rule (new or edit)
  const handleSubmitRule = async (e) => {
    e.preventDefault();
    
    // Debug log for the rule data about to be submitted
    console.log("AUTOMATION UI DEBUG - Submitting rule:", JSON.stringify(newRule, null, 2));
    console.log("AUTOMATION UI DEBUG - Action data fields:", newRule.action_data?.fields);
    console.log("AUTOMATION UI DEBUG - Condition fields:", newRule.conditions);
    console.log("AUTOMATION UI DEBUG - Country field in conditions:", newRule.conditions?.country);
    console.log("AUTOMATION UI DEBUG - Source fields in conditions:", newRule.conditions?.original_source, newRule.conditions?.latest_source);
    console.log("AUTOMATION UI DEBUG - Source match types:", newRule.conditions?.original_source_match_type, newRule.conditions?.latest_source_match_type);
    
    // Validate required fields
    const hasLegacyTrigger = newRule.trigger_event;
    const hasNewTriggers = newRule.triggers && Array.isArray(newRule.triggers) && newRule.triggers.length > 0;
    
    if (!newRule.name || (!hasLegacyTrigger && !hasNewTriggers)) {
      showNotification('Please fill in name and at least one trigger event', 'error');
      return;
    }
    
    // Validate either legacy action or new actions array
    const hasLegacyAction = newRule.action_type && newRule.action_data;
    const hasNewActions = newRule.actions && Array.isArray(newRule.actions) && newRule.actions.length > 0;
    
    if (!hasLegacyAction && !hasNewActions) {
      showNotification('Please add at least one action', 'error');
      return;
    }
    
    // Validate actions if using the new format
    if (hasNewActions) {
      let actionErrors = [];
      
      newRule.actions.forEach((action, index) => {
        if (!action.action_type) {
          actionErrors.push(`Action #${index + 1} is missing an action type`);
        }
        
        // For update_lead, make sure we have at least one field to update
        if (action.action_type === 'update_lead' && 
            (!action.action_data?.fields || Object.keys(action.action_data.fields).length === 0)) {
          actionErrors.push(`Action #${index + 1} (update_lead) has no fields to update`);
        }
        
        // For round_robin_assign, make sure we have a group_name
        if (action.action_type === 'round_robin_assign' && !action.action_data?.group_name) {
          actionErrors.push(`Action #${index + 1} (round_robin_assign) is missing an assignment group`);
        }
        
        // For create_deal, make sure we have required fields
        if (action.action_type === 'create_deal') {
          const hasTitle = action.action_data?.deal_title || action.action_data?.use_lead_email;
          const hasStage = action.action_data?.pipeline_stage;
          
          if (!hasTitle || !hasStage) {
            actionErrors.push(`Action #${index + 1} (create_deal) is missing required fields (title and pipeline stage)`);
          }
        }
        
        // For update_deal, make sure we have required fields
        if (action.action_type === 'update_deal' && !action.action_data?.deal_stage) {
          actionErrors.push(`Action #${index + 1} (update_deal) is missing required stage`);
        }
      });
      
      if (actionErrors.length > 0) {
        showNotification(`Please fix the following errors: ${actionErrors.join(', ')}`, 'error');
        return;
      }
    } else {
      // For legacy format, validate the single action
      
      // For update_lead, make sure we have at least one field to update
      if (newRule.action_type === 'update_lead' && 
          (!newRule.action_data.fields || Object.keys(newRule.action_data.fields).length === 0)) {
        showNotification('Please specify at least one field to update', 'error');
        return;
      }
      
      // For round_robin_assign, make sure we have a group_name
      if (newRule.action_type === 'round_robin_assign' && !newRule.action_data.group_name) {
        showNotification('Please select an assignment group', 'error');
        return;
      }
    }
    
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };
      
      // Prepare rule data for submission
      // Ensure correct data structures for JSON fields
      
      // Clean up conditions - handle empty values and ensure correct structure
      const cleanConditions = { ...newRule.conditions };
      // Remove any undefined or empty string values
      Object.keys(cleanConditions).forEach(key => {
        if (cleanConditions[key] === undefined || cleanConditions[key] === '') {
          delete cleanConditions[key];
        }
      });
      
      // Make sure action_data has the correct structure
      const cleanActionData = { ...newRule.action_data };
      if (newRule.action_type === 'update_lead') {
        // Ensure fields exists and remove any empty values
        cleanActionData.fields = { ...newRule.action_data.fields };
        Object.keys(cleanActionData.fields).forEach(key => {
          if (cleanActionData.fields[key] === undefined || cleanActionData.fields[key] === '') {
            delete cleanActionData.fields[key];
          }
        });
      }
      
      console.log("SUBMISSION DEBUG - Cleaned conditions:", cleanConditions);
      console.log("SUBMISSION DEBUG - Cleaned action data:", cleanActionData);
      
      // Prepare actions array if using new format
      let cleanActions = null;
      if (newRule.actions && Array.isArray(newRule.actions) && newRule.actions.length > 0) {
        cleanActions = newRule.actions.map(action => {
          let cleanActionData = { ...action.action_data };
          
          // Clean action data based on action type
          if (action.action_type === 'update_lead' && cleanActionData.fields) {
            // Remove empty field values
            const cleanFields = { ...cleanActionData.fields };
            Object.keys(cleanFields).forEach(key => {
              if (cleanFields[key] === undefined || cleanFields[key] === '') {
                delete cleanFields[key];
              }
            });
            cleanActionData.fields = cleanFields;
          }
          
          return {
            action_type: action.action_type,
            action_data: cleanActionData
          };
        });
      }
      
      const ruleData = {
        name: newRule.name,
        description: newRule.description,
        trigger_event: newRule.trigger_event,
        triggers: newRule.triggers,             // Add multiple triggers support
        action_type: newRule.action_type,       // For backward compatibility
        conditions: cleanConditions,
        action_data: cleanActionData,           // For backward compatibility
        actions: cleanActions,                  // New array format
        is_active: newRule.is_active
      };
      
      let response;
      
      if (isEditMode) {
        // Update existing rule
        response = await axios.put(
          `/api/automation/rules/${editRuleId}`,
          ruleData,
          config
        );
        
        // Update the rule in the state
        setAutomationRules(automationRules.map(rule => 
          rule.id === editRuleId ? response.data : rule
        ));
        
        showNotification('Automation rule updated successfully', 'success');
      } else {
        // Create new rule
        response = await axios.post(
          '/api/automation/rules',
          ruleData,
          config
        );
        
        // Add the new rule to the state
        setAutomationRules([...automationRules, response.data]);
        
        showNotification('New automation rule created successfully', 'success');
      }
      
      // Reset form and close modal
      setNewRule({
        name: '',
        description: '',
        trigger_event: 'lead_created', // Default trigger event
        action_type: 'update_lead',    // For backward compatibility
        conditions: {},
        action_data: {                 // For backward compatibility
          fields: {}
        },
        actions: [                     // New array for multiple actions
          {
            action_type: 'update_lead',
            action_data: {
              fields: {}
            }
          }
        ],
        is_active: true
      });
      
      setShowRuleModal(false);
      setIsEditMode(false);
      setEditRuleId(null);
      setInitialRuleState(null); // Clear initial state on successful submit
      setLoading(false);
    } catch (err) {
      console.error('Error saving rule:', err);
      showNotification('Failed to save rule: ' + (err.response?.data?.message || err.message), 'error');
      setLoading(false);
    }
  };

  // Format JSON data for display with user-friendly values
  const formatJsonForDisplay = (jsonObj, context = '') => {
    if (!jsonObj || Object.keys(jsonObj).length === 0) {
      return 'None';
    }
    
    // Handle multiple actions array
    if (context === 'actions' && Array.isArray(jsonObj)) {
      return jsonObj.map((action, index) => {
        const actionType = action.action_type.replace(/_/g, ' ');
        let actionDetails = '';
        
        // Process action details based on type
        if (action.action_type === 'update_lead' && action.action_data?.fields) {
          // Enhanced to show field values
          const fields = Object.entries(action.action_data.fields).map(([field, value]) => {
            // Handle special fields
            if (field === 'assigned_to') {
              const user = users.find(u => u.id == value);
              return `${field}: ${user ? user.name : value}`;
            } 
            else if (field === 'country') {
              return `${field}: ${value}`;
            }
            else {
              return `${field}: ${value}`;
            }
          }).join(', ');
          actionDetails = fields ? `(${fields})` : '';
        } else if (action.action_type === 'round_robin_assign' && action.action_data?.group_name) {
          actionDetails = `(${action.action_data.group_name})`;
        } else if (action.action_type === 'create_deal' && action.action_data?.deal_title) {
          const stageName = pipelineStages.find(s => s.id == action.action_data.pipeline_stage)?.name || '';
          actionDetails = `(${action.action_data.deal_title}${stageName ? ' → ' + stageName : ''})`;
        } else if (action.action_type === 'update_deal' && action.action_data?.deal_stage) {
          const stageName = pipelineStages.find(s => s.id == action.action_data.deal_stage)?.name || '';
          actionDetails = stageName ? `(→ ${stageName})` : '';
        } else if (action.action_type === 'send_email' && action.action_data?.template_id) {
          const template = emailTemplates.find(t => t.id == action.action_data.template_id);
          actionDetails = template ? `(${template.name})` : '';
        }
        
        return `${index + 1}. ${actionType}${actionDetails ? ' ' + actionDetails : ''}`;
      }).join('; ');
    }
    
    // Handle the special case for assigned_to in action_data.fields
    if (jsonObj.fields && jsonObj.fields.assigned_to) {
      const assignedToId = jsonObj.fields.assigned_to;
      const assignedUser = users.find(user => user.id == assignedToId);
      
      // Create a copy to avoid mutating the original
      const displayObj = { ...jsonObj };
      displayObj.fields = { ...displayObj.fields };
      
      // Replace ID with user name if found
      if (assignedUser) {
        displayObj.fields.assigned_to = assignedUser.name;
      }
      
      jsonObj = displayObj;
    }
    
    // Handle group_name for round-robin assignment
    if (jsonObj.group_name) {
      const groupName = jsonObj.group_name;
      const group = assignmentGroups.find(g => g.group_name === groupName);
      
      if (group && group.users && group.users.length > 0) {
        const userNames = group.users.map(user => user.name).join(', ');
        return `Group: ${groupName} (${userNames})`;
      }
    }
    
    // Special handling for source arrays
    if (jsonObj.latest_source && Array.isArray(jsonObj.latest_source)) {
      const matchType = jsonObj.latest_source_match_type || 'equals';
      const values = jsonObj.latest_source.join(' OR ');
      return `latest_source (${matchType}): ${values}`;
    }
    
    // Special handling for source arrays (legacy source field)
    if (jsonObj.source && Array.isArray(jsonObj.source)) {
      const matchType = jsonObj.source_match_type || 'equals';
      const values = jsonObj.source.join(' OR ');
      return `source (${matchType}): ${values}`;
    }
    
    return Object.entries(jsonObj)
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          return `${key}: ${value.join(' OR ')}`;
        } else if (typeof value === 'object') {
          return `${key}: ${formatJsonForDisplay(value)}`;
        }
        return `${key}: ${value}`;
      })
      .join(', ');
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium text-gray-900">Automation Rules</h3>
        <button
          onClick={() => {
            // Make sure the action_data structure is properly initialized for update_lead
            const initialRule = {
              name: '',
              description: '',
              trigger_event: 'lead_created', // Default trigger event
              action_type: 'update_lead',    // For backward compatibility
              conditions: {},
              action_data: {                 // For backward compatibility
                fields: {}
              },
              actions: [                     // New array for multiple actions
                {
                  action_type: 'update_lead',
                  action_data: {
                    fields: {}
                  }
                }
              ],
              is_active: true
            };
            
            console.log("Initializing new rule:", initialRule);
            setNewRule(initialRule);
            setInitialRuleState(JSON.parse(JSON.stringify(initialRule))); // Store initial state for comparison
            setIsEditMode(false);
            setEditRuleId(null);
            setShowRuleModal(true);
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 text-sm rounded-md flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          New Rule
        </button>
      </div>
      
      {/* Display error or success messages */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
          {error}
        </div>
      )}
      
      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded">
          {success}
        </div>
      )}
      
      {/* Show loading state */}
      {loading && (
        <div className="mb-4 px-4 py-3 text-blue-600">
          Loading...
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
        <ul className="divide-y divide-gray-200">
          {automationRules.length === 0 ? (
            <li className="p-4 text-center text-gray-500">
              No automation rules found. Create a new rule to get started.
            </li>
          ) : (
            automationRules.map((rule) => (
              <li key={rule.id} className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center">
                      <h4 className="text-md font-medium text-gray-900">{rule.name}</h4>
                      <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${
                        rule.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {rule.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">{rule.description}</p>
                    
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div className="text-xs text-gray-700">
                        <span className="font-medium">Triggers:</span> 
                        <span className="ml-1">
                          {rule.triggers && Array.isArray(rule.triggers) && rule.triggers.length > 0 
                            ? rule.triggers.map(t => t.replace(/_/g, ' ')).join(', ')
                            : rule.trigger_event ? rule.trigger_event.replace(/_/g, ' ') : 'No trigger'}
                        </span>
                      </div>
                      
                      {/* Display actions with preference for the new format */}
                      <div className="text-xs text-gray-700">
                        <span className="font-medium">Actions:</span>
                        <span className="ml-1">
                          {rule.actions && Array.isArray(rule.actions) && rule.actions.length > 0
                            ? formatJsonForDisplay(rule.actions, 'actions')
                            : rule.action_type
                              ? rule.action_type.replace(/_/g, ' ')
                              : 'None'
                          }
                        </span>
                      </div>
                      
                      {rule.conditions && Object.keys(rule.conditions).length > 0 && (
                        <div className="text-xs text-gray-700">
                          <span className="font-medium">Conditions:</span> 
                          <span className="ml-1">{formatJsonForDisplay(rule.conditions)}</span>
                        </div>
                      )}
                      
                      {/* Only show legacy action data if using the old format */}
                      {rule.action_data && (!rule.actions || !Array.isArray(rule.actions) || rule.actions.length === 0) && (
                        <div className="text-xs text-gray-700">
                          <span className="font-medium">Action Details:</span> 
                          <span className="ml-1">{formatJsonForDisplay(rule.action_data)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="ml-4 flex-shrink-0 flex space-x-2">
                    <button
                      onClick={() => handleEditRule(rule)}
                      className="inline-flex items-center px-2 py-1 border border-blue-300 rounded-md text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleToggleRule(rule.id)}
                      className={`inline-flex items-center px-2 py-1 border rounded-md text-xs font-medium ${
                        rule.is_active
                          ? 'border-red-300 text-red-700 bg-red-50 hover:bg-red-100'
                          : 'border-green-300 text-green-700 bg-green-50 hover:bg-green-100'
                      }`}
                    >
                      {rule.is_active ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      onClick={() => handleDeleteRule(rule.id)}
                      className="inline-flex items-center px-2 py-1 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>
      
      {/* Escape Key Listener for Modal */}
      {useEffect(() => {
        if (!showRuleModal) return; // Only run if modal is open

        const handleKeyDown = (event) => {
          if (event.key === 'Escape') {
            handleAttemptClose();
          }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
          document.removeEventListener('keydown', handleKeyDown);
        };
      }, [showRuleModal, handleAttemptClose])} {/* Add handleAttemptClose dependency */}

      {/* Rule Modal (New/Edit) */}
      {showRuleModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Backdrop */}
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={handleAttemptClose}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            {/* Modal Panel */}
            <div 
              className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-7xl sm:w-full border border-gray-100"
              onClick={e => e.stopPropagation()} // Prevent clicks inside modal from closing it
            >
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {isEditMode ? 'Edit Automation Rule' : 'Create New Automation Rule'}
                </h3>
              </div>
              <div className="bg-white px-6 pt-6 pb-6">
                <div className="w-full">
                    <form onSubmit={handleSubmitRule} className="overflow-y-auto max-h-[80vh]">
                      {/* Basic Info Section */}
                      <div className="mb-6 bg-white p-5 rounded-lg shadow-sm border border-gray-100">
                        <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          Rule Information
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                              Rule Name *
                            </label>
                            <input
                              type="text"
                              name="name"
                              id="name"
                              required
                              placeholder="E.g., 'Send course welcome email on payment'"
                              value={newRule.name}
                              onChange={handleNewRuleChange}
                              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md bg-white px-4 py-2.5 transition-all duration-200"
                            />
                          </div>
                          
                          <div>
                            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                              Description
                            </label>
                            <input
                              type="text"
                              name="description"
                              id="description"
                              placeholder="Add a brief description..."
                              value={newRule.description}
                              onChange={handleNewRuleChange}
                              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md bg-white px-4 py-2.5 transition-all duration-200"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Main Workflow Section - Side by side */}
                      <div className="mb-6 grid grid-cols-3 gap-6">
                        {/* Trigger Section */}
                        <div className="bg-gradient-to-b from-indigo-50 to-white p-5 rounded-lg shadow-sm border border-indigo-100 transition-all hover:shadow-md">
                          <h4 className="text-md font-semibold text-indigo-700 mb-4 flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                            Trigger
                          </h4>
                          <div>
                            <label htmlFor="triggers" className="block text-sm font-medium text-gray-700 mb-1">
                              When this happens... * (Select one or more triggers)
                            </label>
                            <select
                              name="triggers"
                              id="triggers"
                              required
                              multiple
                              value={newRule.triggers}
                              onChange={(e) => {
                                const selectedTriggers = Array.from(
                                  e.target.selectedOptions, 
                                  option => option.value
                                );
                                
                                // Update both triggers array and legacy trigger_event (for backward compatibility)
                                setNewRule(prev => ({
                                  ...prev,
                                  triggers: selectedTriggers,
                                  trigger_event: selectedTriggers.length > 0 ? selectedTriggers[0] : ''
                                }));
                              }}
                              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md bg-white px-4 py-2.5 transition-all duration-200 h-32"
                            >
                              <option value="lead_created">Lead Created</option>
                              <option value="lead_updated">Lead Updated</option>
                              <option value="lead_reconverted">Lead Reconverted</option>
                              <option value="deal_created">Deal Created</option>
                              <option value="deal_updated">Deal Updated</option>
                              <option value="deal_stage_changed">Deal Stage Changed</option>
                              <option value="mixpanel_activity">Mixpanel Activity</option>
                            </select>
                            <p className="mt-1 text-xs text-gray-500">
                              Hold Ctrl (Windows) or Cmd (Mac) to select multiple triggers
                            </p>
                          </div>
                        </div>
                        
                        {/* Condition Section */}
                        <div className="bg-gradient-to-b from-blue-50 to-white p-5 rounded-lg shadow-sm border border-blue-100 transition-all hover:shadow-md">
                          <h4 className="text-md font-semibold text-blue-700 mb-4 flex items-center justify-between">
                            <div className="flex items-center">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Conditions
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                // Show condition selection dropdown
                                const dropdown = document.getElementById('condition-dropdown');
                                dropdown.classList.toggle('hidden');
                              }}
                              className="inline-flex items-center px-3 py-1 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                              </svg>
                              Add Condition
                            </button>
                          </h4>
                          
                          {/* Condition type dropdown - initially hidden */}
                          <div id="condition-dropdown" className="hidden mb-4 p-3 bg-white rounded-lg shadow-md border border-gray-200">
                            <h5 className="text-sm font-medium text-gray-700 mb-2">Select a condition type to add:</h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {/* Show source condition for all lead trigger types */}
                              {(newRule.trigger_event === 'lead_created' || newRule.trigger_event === 'lead_updated' || newRule.trigger_event === 'lead_reconverted') && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    // Toggle source condition
                                    setNewRule(prev => {
                                      // Initialize source array if it doesn't exist
                                      const hasSourceCondition = prev.conditions.latest_source !== undefined;
                                      
                                      // Create updated conditions
                                      const updatedConditions = {
                                        ...prev.conditions
                                      };
                                      
                                      // If source doesn't exist, add it; otherwise do nothing (it's already shown)
                                      if (!hasSourceCondition) {
                                        updatedConditions.latest_source = [''];
                                        updatedConditions.latest_source_match_type = 'equals';
                                        // For backward compatibility
                                        updatedConditions.source = [''];
                                        updatedConditions.source_match_type = 'equals';
                                      }
                                      
                                      document.getElementById('condition-dropdown').classList.add('hidden');
                                      
                                      return {
                                        ...prev,
                                        conditions: updatedConditions
                                      };
                                    });
                                  }}
                                  className="text-left px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-blue-50 rounded border border-gray-200 hover:border-blue-200 transition-colors flex items-center"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                  </svg>
                                  Latest Source
                                </button>
                              )}
                              
                              <button
                                type="button"
                                onClick={() => {
                                  // Toggle country condition
                                  setNewRule(prev => {
                                    // Create updated conditions
                                    const updatedConditions = {
                                      ...prev.conditions,
                                      country: prev.conditions.country !== undefined ? prev.conditions.country : []
                                    };
                                    
                                    document.getElementById('condition-dropdown').classList.add('hidden');
                                    
                                    return {
                                      ...prev,
                                      conditions: updatedConditions
                                    };
                                  });
                                }}
                                className="text-left px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-blue-50 rounded border border-gray-200 hover:border-blue-200 transition-colors flex items-center"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                                </svg>
                                Country
                              </button>
                              
                              <button
                                type="button"
                                onClick={() => {
                                  // Toggle phone prefix condition
                                  setNewRule(prev => {
                                    // Create updated conditions
                                    const updatedConditions = {
                                      ...prev.conditions,
                                      phone_prefix: prev.conditions.phone_prefix !== undefined ? prev.conditions.phone_prefix : ''
                                    };
                                    
                                    document.getElementById('condition-dropdown').classList.add('hidden');
                                    
                                    return {
                                      ...prev,
                                      conditions: updatedConditions
                                    };
                                  });
                                }}
                                className="text-left px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-blue-50 rounded border border-gray-200 hover:border-blue-200 transition-colors flex items-center"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                                Phone Prefix
                              </button>
                              
                              {/* Mixpanel Activity Conditions - Show only for mixpanel_activity trigger */}
                              {(newRule.triggers && newRule.triggers.includes('mixpanel_activity')) && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      // Toggle event_name condition
                                      setNewRule(prev => {
                                        const hasEventName = prev.conditions.event_name !== undefined;
                                        const updatedConditions = { ...prev.conditions };
                                        
                                        if (hasEventName) {
                                          delete updatedConditions.event_name;
                                        } else {
                                          updatedConditions.event_name = '';
                                        }
                                        
                                        return {
                                          ...prev,
                                          conditions: updatedConditions
                                        };
                                      });
                                      
                                      // Hide the dropdown
                                      document.getElementById('condition-dropdown').classList.add('hidden');
                                    }}
                                    className="flex items-center p-2 text-sm text-left border border-gray-200 rounded hover:bg-gray-50 transition-colors"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                    Event Name
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => {
                                      // Toggle page_url condition
                                      setNewRule(prev => {
                                        const hasPageUrl = prev.conditions.page_url !== undefined;
                                        const updatedConditions = { ...prev.conditions };
                                        
                                        if (hasPageUrl) {
                                          delete updatedConditions.page_url;
                                          delete updatedConditions.page_url_match_type;
                                        } else {
                                          updatedConditions.page_url = '';
                                          updatedConditions.page_url_match_type = 'contains';
                                        }
                                        
                                        return {
                                          ...prev,
                                          conditions: updatedConditions
                                        };
                                      });
                                      
                                      // Hide the dropdown
                                      document.getElementById('condition-dropdown').classList.add('hidden');
                                    }}
                                    className="flex items-center p-2 text-sm text-left border border-gray-200 rounded hover:bg-gray-50 transition-colors"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Page URL
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => {
                                      // Toggle UTM source condition
                                      setNewRule(prev => {
                                        const hasUtmSource = prev.conditions.utm_source !== undefined;
                                        const updatedConditions = { ...prev.conditions };
                                        
                                        if (hasUtmSource) {
                                          delete updatedConditions.utm_source;
                                        } else {
                                          updatedConditions.utm_source = '';
                                        }
                                        
                                        return {
                                          ...prev,
                                          conditions: updatedConditions
                                        };
                                      });
                                      
                                      // Hide the dropdown
                                      document.getElementById('condition-dropdown').classList.add('hidden');
                                    }}
                                    className="flex items-center p-2 text-sm text-left border border-gray-200 rounded hover:bg-gray-50 transition-colors"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                    UTM Source
                                  </button>
                                </>
                              )}

                              {/* Assigned To Condition - Show for lead triggers */}
                              {(newRule.triggers && (newRule.triggers.includes('lead_created') || 
                                newRule.triggers.includes('lead_updated') || 
                                newRule.triggers.includes('lead_reconverted'))) && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    // Toggle assigned_to condition
                                    setNewRule(prev => {
                                      // Create updated conditions
                                      const updatedConditions = {
                                        ...prev.conditions,
                                        assigned_to: prev.conditions.assigned_to !== undefined ? prev.conditions.assigned_to : ""
                                      };
                                      
                                      document.getElementById('condition-dropdown').classList.add('hidden');
                                      
                                      return {
                                        ...prev,
                                        conditions: updatedConditions
                                      };
                                    });
                                  }}
                                  className="text-left px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-blue-50 rounded border border-gray-200 hover:border-blue-200 transition-colors flex items-center"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                  </svg>
                                  Assigned To
                                </button>
                              )}
                              
                              {/* Deal Stage Condition - Show for all deal-related triggers */}
                              {(newRule.trigger_event === 'deal_created' || newRule.trigger_event === 'deal_updated' || newRule.trigger_event === 'deal_stage_changed') && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    // Toggle deal stage condition
                                    setNewRule(prev => {
                                      // Create updated conditions
                                      const updatedConditions = {
                                        ...prev.conditions,
                                        deal_stage: prev.conditions.deal_stage !== undefined ? prev.conditions.deal_stage : []
                                      };
                                      
                                      document.getElementById('condition-dropdown').classList.add('hidden');
                                      
                                      return {
                                        ...prev,
                                        conditions: updatedConditions
                                      };
                                    });
                                  }}
                                  className="text-left px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-blue-50 rounded border border-gray-200 hover:border-blue-200 transition-colors flex items-center"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                  </svg>
                                  Deal Stage
                                </button>
                              )}
                              
                              {/* Course Condition - Show for all deal-related triggers */}
                              {(newRule.trigger_event === 'deal_created' || newRule.trigger_event === 'deal_updated' || newRule.trigger_event === 'deal_stage_changed') && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    // Toggle course condition
                                    setNewRule(prev => {
                                      // Create updated conditions
                                      const updatedConditions = {
                                        ...prev.conditions,
                                        course_id: prev.conditions.course_id !== undefined ? prev.conditions.course_id : []
                                      };
                                      
                                      document.getElementById('condition-dropdown').classList.add('hidden');
                                      
                                      return {
                                        ...prev,
                                        conditions: updatedConditions
                                      };
                                    });
                                  }}
                                  className="text-left px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-blue-50 rounded border border-gray-200 hover:border-blue-200 transition-colors flex items-center"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                  </svg>
                                  Course
                                </button>
                              )}
                              
                              {/* Previous Stage Condition - Only show for deal_stage_changed */}
                              {newRule.trigger_event === 'deal_stage_changed' && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    // Toggle previous stage condition
                                    setNewRule(prev => {
                                      // Create updated conditions
                                      const updatedConditions = {
                                        ...prev.conditions,
                                        previous_stage: prev.conditions.previous_stage !== undefined ? prev.conditions.previous_stage : []
                                      };
                                      
                                      document.getElementById('condition-dropdown').classList.add('hidden');
                                      
                                      return {
                                        ...prev,
                                        conditions: updatedConditions
                                      };
                                    });
                                  }}
                                  className="text-left px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-blue-50 rounded border border-gray-200 hover:border-blue-200 transition-colors flex items-center"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                                  </svg>
                                  Previous Stage
                                </button>
                              )}
                            </div>
                          </div>
                          
                          {/* No conditions message if no conditions are selected */}
                          {(!newRule.conditions || Object.keys(newRule.conditions).length === 0) && (
                            <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                              <p className="text-center">No conditions selected. This rule will run for <strong>all {(newRule.triggers || []).map(t => t.replace(/_/g, ' ')).join(' or ')} events</strong>.</p>
                              <p className="text-center text-sm mt-1">Click "Add Condition" to narrow down when this rule will run.</p>
                            </div>
                          )}
                          
                          {/* Selected conditions */}
                          <div className="space-y-6">
                            {/* Source Condition - Only show if added for lead trigger types */}
                            {(newRule.trigger_event === 'lead_created' || newRule.trigger_event === 'lead_updated' || newRule.trigger_event === 'lead_reconverted') && newRule.conditions.latest_source !== undefined && (
                              <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-200 relative">
                                <div className="flex justify-between items-start mb-3">
                                  <div className="flex items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                    <h5 className="text-sm font-medium text-gray-700">Latest Source</h5>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      // Remove source condition
                                      setNewRule(prev => {
                                        const updatedConditions = { ...prev.conditions };
                                        delete updatedConditions.latest_source;
                                        delete updatedConditions.latest_source_match_type;
                                        delete updatedConditions.source; // Also remove legacy fields
                                        delete updatedConditions.source_match_type;
                                        
                                        return {
                                          ...prev,
                                          conditions: updatedConditions
                                        };
                                      });
                                    }}
                                    className="text-gray-400 hover:text-red-500"
                                    aria-label="Remove condition"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-2 mb-2">
                                  <select
                                    id="condition_latest_source_match_type"
                                    value={newRule.conditions.latest_source_match_type || 'equals'}
                                    onChange={(e) => {
                                      const matchType = e.target.value;
                                      
                                      // Ensure we're keeping the latest_source value when changing the match type
                                      setNewRule(prev => {
                                        const updatedConditions = {
                                          ...prev.conditions,
                                          latest_source_match_type: matchType
                                        };
                                        
                                        // Ensure latest_source value remains intact
                                        if (prev.conditions.latest_source) {
                                          updatedConditions.latest_source = prev.conditions.latest_source;
                                        }
                                        
                                        // Also update old fields for backward compatibility
                                        updatedConditions.source_match_type = matchType;
                                        
                                        return {
                                          ...prev,
                                          conditions: updatedConditions
                                        };
                                      });
                                    }}
                                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md bg-white transition-all duration-200"
                                  >
                                    <option value="equals">Equals</option>
                                    <option value="begins_with">Begins with</option>
                                    <option value="contains">Contains</option>
                                  </select>
                                  <div className="flex">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        // Add a new empty source condition
                                        setNewRule(prev => {
                                          // Initialize source conditions array if needed
                                          let currentSources = prev.conditions.latest_source || [];
                                          if (!Array.isArray(currentSources)) {
                                            // Convert single string value to array
                                            currentSources = currentSources ? [currentSources] : [];
                                          }
                                          
                                          // Add an empty condition
                                          const updatedSources = [...currentSources, ''];
                                          
                                          // Update both latest_source and legacy source fields
                                          return {
                                            ...prev,
                                            conditions: {
                                              ...prev.conditions,
                                              latest_source: updatedSources,
                                              source: updatedSources // For backward compatibility
                                            }
                                          };
                                        });
                                      }}
                                      className="ml-2 flex items-center justify-center px-3 py-1 border border-green-300 text-sm font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                      </svg>
                                      <span className="ml-1">Add Source Value</span>
                                    </button>
                                  </div>
                                </div>
                                
                                {/* Source Conditions List */}
                                <div className="space-y-2">
                                  {/* Convert to array if it's a string (backward compatibility) */}
                                  {(() => {
                                    let sourcesArray = newRule.conditions.latest_source || [];
                                    if (!Array.isArray(sourcesArray)) {
                                      sourcesArray = sourcesArray ? [sourcesArray] : [];
                                    }
                                    
                                    // If empty, show at least one input field
                                    if (sourcesArray.length === 0) {
                                      sourcesArray = [''];
                                    }
                                    
                                    return sourcesArray.map((source, index) => (
                                      <div key={index} className="flex items-center">
                                        <input
                                          type="text"
                                          value={source || ''}
                                          onChange={(e) => {
                                            const sourceValue = e.target.value;
                                            
                                            setNewRule(prev => {
                                              // Get current sources array
                                              let currentSources = prev.conditions.latest_source || [];
                                              if (!Array.isArray(currentSources)) {
                                                // Convert single string value to array
                                                currentSources = currentSources ? [currentSources] : [];
                                              }
                                              
                                              // Update the specific index
                                              const updatedSources = [...currentSources];
                                              updatedSources[index] = sourceValue;
                                              
                                              // Filter out empty values for clarity in storage
                                              // But keep at least one entry
                                              const filteredSources = updatedSources.filter(s => s.trim() !== '');
                                              const finalSources = filteredSources.length > 0 ? filteredSources : [''];
                                              
                                              // Update both latest_source and legacy source fields
                                              return {
                                                ...prev,
                                                conditions: {
                                                  ...prev.conditions,
                                                  latest_source: finalSources,
                                                  source: finalSources // For backward compatibility
                                                }
                                              };
                                            });
                                          }}
                                          placeholder={`Source value ${index + 1} (Facebook, Website, etc.)`}
                                          className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md bg-white px-3 py-2 transition-all duration-200"
                                        />
                                        
                                        {/* Remove button - only show if there's more than one source */}
                                        {sourcesArray.length > 1 && (
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setNewRule(prev => {
                                                // Get current sources array
                                                let currentSources = prev.conditions.latest_source || [];
                                                if (!Array.isArray(currentSources)) {
                                                  currentSources = currentSources ? [currentSources] : [];
                                                }
                                                
                                                // Remove the specific index
                                                const updatedSources = currentSources.filter((_, i) => i !== index);
                                                
                                                // Update both latest_source and legacy source fields
                                                return {
                                                  ...prev,
                                                  conditions: {
                                                    ...prev.conditions,
                                                    latest_source: updatedSources.length > 0 ? updatedSources : undefined,
                                                    source: updatedSources.length > 0 ? updatedSources : undefined
                                                  }
                                                };
                                              });
                                            }}
                                            className="ml-2 flex items-center justify-center p-1 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100"
                                          >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                          </button>
                                        )}
                                      </div>
                                    ));
                                  })()}
                                </div>
                                
                                <p className="mt-2 text-xs text-gray-500">
                                  Match leads where latest source equals, begins with, or contains any of these values (OR logic).
                                </p>
                              </div>
                            )}
                            
                            {/* Country Condition - Only show if added */}
                            {newRule.conditions.country !== undefined && (
                              <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-200 relative">
                                <div className="flex justify-between items-start mb-3">
                                  <div className="flex items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                                    </svg>
                                    <h5 className="text-sm font-medium text-gray-700">Countries</h5>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      // Remove country condition
                                      setNewRule(prev => {
                                        const updatedConditions = { ...prev.conditions };
                                        delete updatedConditions.country;
                                        
                                        return {
                                          ...prev,
                                          conditions: updatedConditions
                                        };
                                      });
                                    }}
                                    className="text-gray-400 hover:text-red-500"
                                    aria-label="Remove condition"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                                
                                <select
                                  id="condition_countries"
                                  multiple
                                  value={Array.isArray(newRule.conditions.country) ? newRule.conditions.country : 
                                        newRule.conditions.country ? [newRule.conditions.country] : []}
                                  onChange={(e) => {
                                    const selectedCountries = Array.from(
                                      e.target.selectedOptions, 
                                      option => option.value
                                    );
                                    
                                    setNewRule(prev => ({
                                      ...prev,
                                      conditions: {
                                        ...prev.conditions,
                                        country: selectedCountries.length > 0 ? selectedCountries : undefined
                                      }
                                    }));
                                  }}
                                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md h-20 bg-white transition-all duration-200"
                                >
                                  <option value="Norway">Norway</option>
                                  <option value="Sweden">Sweden</option>
                                  <option value="Germany">Germany</option>
                                  <option value="Austria">Austria</option>
                                  <option value="Switzerland">Switzerland</option>
                                  <option value="Luxembourg">Luxembourg</option>
                                </select>
                                <p className="mt-1 text-xs text-gray-500">
                                  Select one or more, or none to match any.
                                </p>
                              </div>
                            )}
                            
                            {/* Phone Prefix Condition - Only show if added */}
                            {newRule.conditions.phone_prefix !== undefined && (
                              <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-200 relative">
                                <div className="flex justify-between items-start mb-3">
                                  <div className="flex items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                    </svg>
                                    <h5 className="text-sm font-medium text-gray-700">Phone Number Prefix</h5>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      // Remove phone prefix condition
                                      setNewRule(prev => {
                                        const updatedConditions = { ...prev.conditions };
                                        delete updatedConditions.phone_prefix;
                                        
                                        return {
                                          ...prev,
                                          conditions: updatedConditions
                                        };
                                      });
                                    }}
                                    className="text-gray-400 hover:text-red-500"
                                    aria-label="Remove condition"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                                
                                <input
                                  type="text"
                                  id="condition_phone_prefix"
                                  value={newRule.conditions.phone_prefix || ''}
                                  onChange={(e) => {
                                    const prefixValue = e.target.value;
                                    setNewRule(prev => ({
                                      ...prev,
                                      conditions: {
                                        ...prev.conditions,
                                        phone_prefix: prefixValue || undefined
                                      }
                                    }));
                                  }}
                                  placeholder="+47 (Norway), +46 (Sweden), etc."
                                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md bg-white px-3 py-2 transition-all duration-200"
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                  Match leads with phone numbers starting with this prefix (country code).
                                </p>
                              </div>
                            )}
                            
                            {/* Assigned To Condition - Only show if added */}
                            {newRule.conditions.assigned_to !== undefined && (
                              <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-200 relative">
                                <div className="flex justify-between items-start mb-3">
                                  <div className="flex items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                    <h5 className="text-sm font-medium text-gray-700">Assigned To</h5>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      // Remove assigned_to condition
                                      setNewRule(prev => {
                                        const updatedConditions = { ...prev.conditions };
                                        delete updatedConditions.assigned_to;
                                        
                                        return {
                                          ...prev,
                                          conditions: updatedConditions
                                        };
                                      });
                                    }}
                                    className="text-gray-400 hover:text-red-500"
                                    aria-label="Remove condition"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                                
                                <select
                                  id="condition_assigned_to"
                                  value={newRule.conditions.assigned_to}
                                  onChange={e => {
                                    const value = e.target.value;
                                    setNewRule(prev => ({
                                      ...prev,
                                      conditions: {
                                        ...prev.conditions,
                                        assigned_to: value
                                      }
                                    }));
                                  }}
                                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                >
                                  <option value="">Any user</option>
                                  <option value="null">Unassigned</option>
                                  {users.map(user => (
                                    <option key={user.id} value={user.id}>
                                      {user.name}
                                    </option>
                                  ))}
                                </select>
                                <p className="mt-1 text-xs text-gray-500">
                                  Select a user to match leads assigned to that specific user.
                                </p>
                              </div>
                            )}
                            
                            {/* Mixpanel Event Name Condition */}
                            {newRule.conditions.event_name !== undefined && (
                              <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-200 relative">
                                <div className="flex justify-between items-start mb-3">
                                  <div className="flex items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                    <h5 className="text-sm font-medium text-gray-700">Mixpanel Event Name</h5>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setNewRule(prev => {
                                        const updatedConditions = { ...prev.conditions };
                                        delete updatedConditions.event_name;
                                        return {
                                          ...prev,
                                          conditions: updatedConditions
                                        };
                                      });
                                    }}
                                    className="text-gray-400 hover:text-red-500"
                                    title="Remove condition"
                                  >
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                                
                                <input
                                  type="text"
                                  placeholder="e.g., Form Submit, Sidebar click"
                                  value={newRule.conditions.event_name || ''}
                                  onChange={(e) => {
                                    setNewRule(prev => ({
                                      ...prev,
                                      conditions: {
                                        ...prev.conditions,
                                        event_name: e.target.value
                                      }
                                    }));
                                  }}
                                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                  Exact event name from Mixpanel (case sensitive)
                                </p>
                              </div>
                            )}

                            {/* Mixpanel Page URL Condition */}
                            {newRule.conditions.page_url !== undefined && (
                              <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-200 relative">
                                <div className="flex justify-between items-start mb-3">
                                  <div className="flex items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <h5 className="text-sm font-medium text-gray-700">Page URL</h5>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setNewRule(prev => {
                                        const updatedConditions = { ...prev.conditions };
                                        delete updatedConditions.page_url;
                                        delete updatedConditions.page_url_match_type;
                                        return {
                                          ...prev,
                                          conditions: updatedConditions
                                        };
                                      });
                                    }}
                                    className="text-gray-400 hover:text-red-500"
                                    title="Remove condition"
                                  >
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-2 mb-2">
                                  <select
                                    value={newRule.conditions.page_url_match_type || 'contains'}
                                    onChange={(e) => {
                                      setNewRule(prev => ({
                                        ...prev,
                                        conditions: {
                                          ...prev.conditions,
                                          page_url_match_type: e.target.value
                                        }
                                      }));
                                    }}
                                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                  >
                                    <option value="contains">Contains</option>
                                    <option value="equals">Equals</option>
                                    <option value="starts_with">Starts with</option>
                                    <option value="ends_with">Ends with</option>
                                  </select>
                                  <input
                                    type="text"
                                    placeholder="e.g., /pricing, checkout"
                                    value={newRule.conditions.page_url || ''}
                                    onChange={(e) => {
                                      setNewRule(prev => ({
                                        ...prev,
                                        conditions: {
                                          ...prev.conditions,
                                          page_url: e.target.value
                                        }
                                      }));
                                    }}
                                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                  />
                                </div>
                                <p className="mt-1 text-xs text-gray-500">
                                  URL or path the user visited during the event
                                </p>
                              </div>
                            )}

                            {/* Mixpanel UTM Source Condition */}
                            {newRule.conditions.utm_source !== undefined && (
                              <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-200 relative">
                                <div className="flex justify-between items-start mb-3">
                                  <div className="flex items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                    <h5 className="text-sm font-medium text-gray-700">UTM Source</h5>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setNewRule(prev => {
                                        const updatedConditions = { ...prev.conditions };
                                        delete updatedConditions.utm_source;
                                        return {
                                          ...prev,
                                          conditions: updatedConditions
                                        };
                                      });
                                    }}
                                    className="text-gray-400 hover:text-red-500"
                                    title="Remove condition"
                                  >
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                                
                                <input
                                  type="text"
                                  placeholder="e.g., google, facebook, email"
                                  value={newRule.conditions.utm_source || ''}
                                  onChange={(e) => {
                                    setNewRule(prev => ({
                                      ...prev,
                                      conditions: {
                                        ...prev.conditions,
                                        utm_source: e.target.value
                                      }
                                    }));
                                  }}
                                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                  Marketing campaign source from UTM parameters
                                </p>
                              </div>
                            )}
                            
                            {/* Deal Stage Condition - Only show if added */}
                            {newRule.conditions.service_type !== undefined && (
                              <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-200 relative">
                                <div className="flex justify-between items-start mb-3">
                                  <div className="flex items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                    </svg>
                                    <h5 className="text-sm font-medium text-gray-700">Service Types</h5>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      // Remove service type condition
                                      setNewRule(prev => {
                                        const updatedConditions = { ...prev.conditions };
                                        delete updatedConditions.service_type;
                                        
                                        return {
                                          ...prev,
                                          conditions: updatedConditions
                                        };
                                      });
                                    }}
                                    className="text-gray-400 hover:text-red-500"
                                    aria-label="Remove condition"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                                
                                <select
                                  id="condition_service_types"
                                  multiple
                                  value={Array.isArray(newRule.conditions.service_type) ? newRule.conditions.service_type : 
                                        newRule.conditions.service_type ? [newRule.conditions.service_type] : []}
                                  onChange={(e) => {
                                    const selectedTypes = Array.from(
                                      e.target.selectedOptions, 
                                      option => option.value
                                    );
                                    
                                    setNewRule(prev => ({
                                      ...prev,
                                      conditions: {
                                        ...prev.conditions,
                                        service_type: selectedTypes.length > 0 ? selectedTypes : undefined
                                      }
                                    }));
                                  }}
                                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md h-20"
                                >
                                  <option value="education">Educational Course</option>
                                  <option value="consulting">Consulting Service</option>
                                  <option value="product">Product</option>
                                  <option value="subscription">Subscription</option>
                                  <option value="other">Other Service</option>
                                </select>
                              </div>
                            )}
                            
                            {newRule.conditions.course_id !== undefined && (
                              <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-200 relative">
                                <div className="flex justify-between items-start mb-3">
                                  <div className="flex items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                    </svg>
                                    <h5 className="text-sm font-medium text-gray-700">Courses</h5>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      // Remove course condition
                                      setNewRule(prev => {
                                        const updatedConditions = { ...prev.conditions };
                                        delete updatedConditions.course_id;
                                        
                                        return {
                                          ...prev,
                                          conditions: updatedConditions
                                        };
                                      });
                                    }}
                                    className="text-gray-400 hover:text-red-500"
                                    aria-label="Remove condition"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                                
                                <select
                                  id="condition_courses"
                                  multiple
                                  value={Array.isArray(newRule.conditions.course_id) ? newRule.conditions.course_id : 
                                        newRule.conditions.course_id ? [newRule.conditions.course_id] : []}
                                  onChange={(e) => {
                                    const selectedCourses = Array.from(
                                      e.target.selectedOptions, 
                                      option => option.value
                                    );
                                    
                                    setNewRule(prev => ({
                                      ...prev,
                                      conditions: {
                                        ...prev.conditions,
                                        course_id: selectedCourses.length > 0 ? selectedCourses : undefined
                                      }
                                    }));
                                  }}
                                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md h-20"
                                >
                                  {courses.length > 0 ? (
                                    courses.map(course => (
                                      <option key={course.id} value={course.id}>
                                        {course.name}
                                        {course.service_type === 'education' && ` - ${course.age_group} - Level ${course.level}`}
                                      </option>
                                    ))
                                  ) : (
                                    <option disabled>No courses available</option>
                                  )}
                                </select>
                              </div>
                            )}
                            
                            {newRule.conditions.deal_stage !== undefined && (
                              <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-200 relative">
                                <div className="flex justify-between items-start mb-3">
                                  <div className="flex items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                    <h5 className="text-sm font-medium text-gray-700">Deal Stages</h5>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      // Remove deal stage condition
                                      setNewRule(prev => {
                                        const updatedConditions = { ...prev.conditions };
                                        delete updatedConditions.deal_stage;
                                        
                                        return {
                                          ...prev,
                                          conditions: updatedConditions
                                        };
                                      });
                                    }}
                                    className="text-gray-400 hover:text-red-500"
                                    aria-label="Remove condition"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                                
                                <select
                                  id="condition_deal_stages"
                                  multiple
                                  value={Array.isArray(newRule.conditions.deal_stage) ? newRule.conditions.deal_stage : 
                                        newRule.conditions.deal_stage ? [newRule.conditions.deal_stage] : []}
                                  onChange={(e) => {
                                    const selectedStages = Array.from(
                                      e.target.selectedOptions, 
                                      option => option.value
                                    );
                                    
                                    setNewRule(prev => ({
                                      ...prev,
                                      conditions: {
                                        ...prev.conditions,
                                        deal_stage: selectedStages.length > 0 ? selectedStages : undefined
                                      }
                                    }));
                                  }}
                                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md h-20"
                                >
                                  {pipelineStages.length > 0 ? (
                                    pipelineStages.map(stage => (
                                      <option key={stage.id} value={stage.name}>
                                        {stage.name}
                                      </option>
                                    ))
                                  ) : (
                                    // Fallback options if API call fails
                                    <>
                                      <option value="Awaiting Payment">Awaiting Payment</option>
                                      <option value="Payment Received">Payment Received</option>
                                      <option value="Course Complete">Course Complete</option>
                                      <option value="Refunded">Refunded</option>
                                      <option value="Withdrawn">Withdrawn</option>
                                    </>
                                  )}
                                </select>
                                <p className="mt-1 text-xs text-gray-500">
                                  Select one or more stages to match deals in these stages, or none to match any stage.
                                </p>
                              </div>
                            )}
                            
                            {/* Previous Stage Condition - Only show if added */}
                            {newRule.conditions.previous_stage !== undefined && (
                              <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-200 relative">
                                <div className="flex justify-between items-start mb-3">
                                  <div className="flex items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                                    </svg>
                                    <h5 className="text-sm font-medium text-gray-700">From Stage (Previous)</h5>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      // Remove previous stage condition
                                      setNewRule(prev => {
                                        const updatedConditions = { ...prev.conditions };
                                        delete updatedConditions.previous_stage;
                                        
                                        return {
                                          ...prev,
                                          conditions: updatedConditions
                                        };
                                      });
                                    }}
                                    className="text-gray-400 hover:text-red-500"
                                    aria-label="Remove condition"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                                
                                <select
                                  id="condition_previous_stage"
                                  multiple
                                  value={Array.isArray(newRule.conditions.previous_stage) ? newRule.conditions.previous_stage : 
                                        newRule.conditions.previous_stage ? [newRule.conditions.previous_stage] : []}
                                  onChange={(e) => {
                                    const selectedStages = Array.from(
                                      e.target.selectedOptions, 
                                      option => option.value
                                    );
                                    
                                    setNewRule(prev => ({
                                      ...prev,
                                      conditions: {
                                        ...prev.conditions,
                                        previous_stage: selectedStages.length > 0 ? selectedStages : undefined
                                      }
                                    }));
                                  }}
                                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md h-20"
                                >
                                  {pipelineStages.length > 0 ? (
                                    pipelineStages.map(stage => (
                                      <option key={stage.id} value={stage.name}>
                                        {stage.name}
                                      </option>
                                    ))
                                  ) : (
                                    // Fallback options if API call fails
                                    <>
                                      <option value="Awaiting Payment">Awaiting Payment</option>
                                      <option value="Payment Received">Payment Received</option>
                                      <option value="Course Complete">Course Complete</option>
                                      <option value="Refunded">Refunded</option>
                                      <option value="Withdrawn">Withdrawn</option>
                                    </>
                                  )}
                                </select>
                                <p className="mt-1 text-xs text-gray-500">
                                  Select one or more previous stages, or none to match any starting stage.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Actions Section */}
                        <div className="bg-gradient-to-b from-green-50 to-white p-5 rounded-lg shadow-sm border border-green-100 transition-all hover:shadow-md">
                          <div className="flex justify-between items-center mb-4">
                            <h4 className="text-md font-semibold text-green-700 flex items-center">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                              </svg>
                              Actions
                            </h4>
                            <button
                              type="button"
                              onClick={() => {
                                setNewRule(prev => {
                                  // Add a new action to the actions array
                                  const updatedActions = [...prev.actions, {
                                    action_type: 'update_lead',
                                    action_data: {
                                      fields: {}
                                    }
                                  }];
                                  
                                  return {
                                    ...prev,
                                    actions: updatedActions
                                  };
                                });
                              }}
                              className="inline-flex items-center px-3 py-1 border border-green-300 text-sm font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                              </svg>
                              Add Action
                            </button>
                          </div>
                          
                          {/* Multiple Actions List */}
                          <div className="space-y-3">
                            {newRule.actions.map((action, actionIndex) => (
                              <div 
                                key={actionIndex} 
                                className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm"
                              >
                                <div className="flex justify-between items-center mb-3">
                                  <h5 className="text-sm font-semibold text-gray-700 flex items-center">
                                    <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-0.5 rounded-full mr-2">
                                      {actionIndex + 1}
                                    </span>
                                    Action
                                  </h5>
                                  
                                  {/* Only show remove button if there's more than one action */}
                                  {newRule.actions.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setNewRule(prev => {
                                          const updatedActions = [...prev.actions];
                                          updatedActions.splice(actionIndex, 1);
                                          return {
                                            ...prev,
                                            actions: updatedActions
                                          };
                                        });
                                      }}
                                      className="text-gray-400 hover:text-red-500"
                                      aria-label="Remove action"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                  )}
                                </div>
                                
                                <div>
                                  <label htmlFor={`action_type_${actionIndex}`} className="block text-sm font-medium text-gray-700 mb-1">
                                    Do this... *
                                  </label>
                                  <select
                                    id={`action_type_${actionIndex}`}
                                    required
                                    value={action.action_type}
                                    onChange={(e) => {
                                      const actionType = e.target.value;
                                      console.log(`Changed action ${actionIndex} type to:`, actionType);
                                      
                                      // Prepare the appropriate action_data structure based on type
                                      let actionData = {};
                                      if (actionType === "update_lead") {
                                        actionData = { fields: {} };
                                      } else if (actionType === "create_deal") {
                                        actionData = { 
                                          deal_title: "",
                                          deal_value: "",
                                          pipeline_stage: ""
                                        };
                                      } else if (actionType === "update_deal") {
                                        actionData = { 
                                          deal_stage: ""
                                        };
                                      } else if (actionType === "round_robin_assign") {
                                        actionData = { group_name: "" };
                                      } else if (actionType === "send_email") {
                                        actionData = { 
                                          template_id: "", 
                                          smtp_profile_id: ""
                                        };
                                      } else if (actionType === "send_chat_notification") {
                                        actionData = { 
                                          message_template: "", 
                                          recipient_id: ""  // Optional, null means team chat
                                        };
                                      }
                                      
                                      setNewRule(prev => {
                                        const updatedActions = [...prev.actions];
                                        updatedActions[actionIndex] = {
                                          action_type: actionType,
                                          action_data: actionData
                                        };
                                        
                                        return {
                                          ...prev,
                                          actions: updatedActions
                                        };
                                      });
                                    }}
                                    className="shadow-sm focus:ring-green-500 focus:border-green-500 block w-full sm:text-sm border-gray-300 rounded-md bg-white px-4 py-2.5 transition-all duration-200"
                                  >
                                    <option value="update_lead">Update Lead</option>
                                    <option value="create_deal">Create Deal</option>
                                    <option value="update_deal">Update Deal</option>
                                    <option value="create_task">Create Task</option>
                                    <option value="send_notification">Send Notification</option>
                                    <option value="send_chat_notification">Send Chat Notification</option>
                                    <option value="round_robin_assign">Round-Robin Assignment</option>
                                    <option value="send_email">Send Email</option>
                                  </select>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      {/* Action Settings for Multiple Actions */}
                      {newRule.actions.map((action, actionIndex) => (
                        <div key={`action-settings-${actionIndex}`} className="mb-6 bg-white p-5 rounded-lg shadow-sm border border-gray-100">
                          <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                            </svg>
                            <span className="flex items-center">
                              Action <span className="ml-2 bg-green-100 text-green-800 text-xs font-medium px-2 py-0.5 rounded-full">{actionIndex + 1}</span>
                            </span> Settings
                          </h4>
                          
                          {action.action_type === 'update_lead' && (
                            <div className="space-y-3">
                              {/* Assigned To Field */}
                              <div>
                                <label htmlFor={`action_${actionIndex}_assigned_to`} className="block text-sm font-medium text-gray-700 mb-1">
                                  Assign To
                                </label>
                                <select
                                  id={`action_${actionIndex}_assigned_to`}
                                  value={action.action_data.fields?.assigned_to || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setNewRule(prev => {
                                      const updatedActions = [...prev.actions];
                                      const updatedAction = {...updatedActions[actionIndex]};
                                      
                                      // Ensure action_data and fields are initialized
                                      if (!updatedAction.action_data) updatedAction.action_data = {};
                                      if (!updatedAction.action_data.fields) updatedAction.action_data.fields = {};
                                      
                                      // Update the field
                                      updatedAction.action_data.fields.assigned_to = value === "" ? undefined : value;
                                      updatedActions[actionIndex] = updatedAction;
                                      
                                      return {
                                        ...prev,
                                        actions: updatedActions
                                      };
                                    });
                                  }}
                                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                >
                                  <option value="">Don't Change</option>
                                  <option value="null">Unassign</option>
                                  {users.map(user => (
                                    <option key={user.id} value={user.id}>
                                      {user.name}
                                    </option>
                                  ))}
                                </select>
                                <p className="mt-1 text-xs text-gray-500">
                                  Who should this lead be assigned to?
                                </p>
                              </div>
                              
                              {/* Status Field */}
                              <div>
                                <label htmlFor={`action_${actionIndex}_status`} className="block text-sm font-medium text-gray-700 mb-1">
                                  Change Status
                                </label>
                                <select
                                  id={`action_${actionIndex}_status`}
                                  value={action.action_data.fields?.status || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setNewRule(prev => {
                                      const updatedActions = [...prev.actions];
                                      const updatedAction = {...updatedActions[actionIndex]};
                                      
                                      // Ensure action_data and fields are initialized
                                      if (!updatedAction.action_data) updatedAction.action_data = {};
                                      if (!updatedAction.action_data.fields) updatedAction.action_data.fields = {};
                                      
                                      // Update the field
                                      updatedAction.action_data.fields.status = value === "" ? undefined : value;
                                      updatedActions[actionIndex] = updatedAction;
                                      
                                      return {
                                        ...prev,
                                        actions: updatedActions
                                      };
                                    });
                                  }}
                                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                >
                                  <option value="">Don't Change</option>
                                  <option value="new">New</option>
                                  <option value="connected">Connected</option>
                                  <option value="HOT">HOT</option>
                                  <option value="disqualified">Disqualified</option>
                                  <option value="converted">Converted</option>
                                  <option value="unreachable">Unreachable</option>
                                </select>
                              </div>
                              
                              {/* Country Field */}
                              <div>
                                <label htmlFor={`action_${actionIndex}_country`} className="block text-sm font-medium text-gray-700 mb-1">
                                  Set Country
                                </label>
                                <select
                                  id={`action_${actionIndex}_country`}
                                  value={action.action_data.fields?.country || ''}
                                  onChange={(e) => {
                                    const selectedCountry = e.target.value;
                                    console.log(`COUNTRY DROPDOWN DEBUG (Action ${actionIndex}) - Selected value:`, selectedCountry);
                                    
                                    // Update the action's country field
                                    setNewRule(prev => {
                                      const updatedActions = [...prev.actions];
                                      const updatedAction = {...updatedActions[actionIndex]};
                                      
                                      // Ensure action_data and fields are initialized
                                      if (!updatedAction.action_data) updatedAction.action_data = {};
                                      if (!updatedAction.action_data.fields) updatedAction.action_data.fields = {};
                                      
                                      // Update the field
                                      updatedAction.action_data.fields.country = selectedCountry === "" ? undefined : selectedCountry;
                                      updatedActions[actionIndex] = updatedAction;
                                      
                                      return {
                                        ...prev,
                                        actions: updatedActions
                                      };
                                    });
                                  }}
                                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                >
                                  <option value="">Don't Change</option>
                                  <option value="Norway">Norway</option>
                                  <option value="Sweden">Sweden</option>
                                  <option value="Germany">Germany</option>
                                  <option value="Austria">Austria</option>
                                  <option value="Switzerland">Switzerland</option>
                                  <option value="Luxembourg">Luxembourg</option>
                                </select>
                              </div>
                            </div>
                          )}
                        
                          {action.action_type === 'create_task' && (
                            <div className="space-y-3">
                              <div>
                                <label htmlFor={`action_${actionIndex}_title`} className="block text-sm font-medium text-gray-700 mb-1">
                                  Task Title *
                                </label>
                                <input
                                  type="text"
                                  id={`action_${actionIndex}_title`}
                                  placeholder="Call customer"
                                  value={action.action_data?.title || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setNewRule(prev => {
                                      const updatedActions = [...prev.actions];
                                      const updatedAction = {...updatedActions[actionIndex]};
                                      
                                      // Ensure action_data is initialized
                                      if (!updatedAction.action_data) updatedAction.action_data = {};
                                      
                                      // Update the field
                                      updatedAction.action_data.title = value;
                                      updatedActions[actionIndex] = updatedAction;
                                      
                                      return {
                                        ...prev,
                                        actions: updatedActions
                                      };
                                    });
                                  }}
                                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                />
                              </div>
                              
                              <div>
                                <label htmlFor={`action_${actionIndex}_description`} className="block text-sm font-medium text-gray-700 mb-1">
                                  Description
                                </label>
                                <input
                                  type="text"
                                  id={`action_${actionIndex}_description`}
                                  placeholder="Follow up with customer about..."
                                  value={action.action_data?.description || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setNewRule(prev => {
                                      const updatedActions = [...prev.actions];
                                      const updatedAction = {...updatedActions[actionIndex]};
                                      
                                      // Ensure action_data is initialized
                                      if (!updatedAction.action_data) updatedAction.action_data = {};
                                      
                                      // Update the field
                                      updatedAction.action_data.description = value;
                                      updatedActions[actionIndex] = updatedAction;
                                      
                                      return {
                                        ...prev,
                                        actions: updatedActions
                                      };
                                    });
                                  }}
                                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                />
                              </div>
                              
                              <div>
                                <label htmlFor={`action_${actionIndex}_assigned_to`} className="block text-sm font-medium text-gray-700 mb-1">
                                  Assign Task To
                                </label>
                                <select
                                  id={`action_${actionIndex}_assigned_to`}
                                  value={action.action_data?.assigned_to || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setNewRule(prev => {
                                      const updatedActions = [...prev.actions];
                                      const updatedAction = {...updatedActions[actionIndex]};
                                      
                                      // Ensure action_data is initialized
                                      if (!updatedAction.action_data) updatedAction.action_data = {};
                                      
                                      // Update the field
                                      updatedAction.action_data.assigned_to = value;
                                      updatedActions[actionIndex] = updatedAction;
                                      
                                      return {
                                        ...prev,
                                        actions: updatedActions
                                      };
                                    });
                                  }}
                                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                >
                                  <option value="">Same as Lead Owner</option>
                                  {users.map(user => (
                                    <option key={user.id} value={user.id}>
                                      {user.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          )}
                          
                          {action.action_type === 'send_notification' && (
                            <div className="space-y-3">
                              <div>
                                <label htmlFor={`action_${actionIndex}_recipients`} className="block text-sm font-medium text-gray-700 mb-1">
                                  Recipients
                                </label>
                                <select
                                  id={`action_${actionIndex}_recipients`}
                                  value={action.action_data?.recipients || 'lead_owner'}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setNewRule(prev => {
                                      const updatedActions = [...prev.actions];
                                      const updatedAction = {...updatedActions[actionIndex]};
                                      
                                      // Ensure action_data is initialized
                                      if (!updatedAction.action_data) updatedAction.action_data = {};
                                      
                                      // Update the field
                                      updatedAction.action_data.recipients = value;
                                      updatedActions[actionIndex] = updatedAction;
                                      
                                      return {
                                        ...prev,
                                        actions: updatedActions
                                      };
                                    });
                                  }}
                                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                >
                                  <option value="lead_owner">Lead Owner</option>
                                  <option value="admin">Admin</option>
                                </select>
                              </div>
                              
                              <div>
                                <label htmlFor={`action_${actionIndex}_message`} className="block text-sm font-medium text-gray-700 mb-1">
                                  Message
                                </label>
                                <textarea
                                  id={`action_${actionIndex}_message`}
                                  rows="2"
                                  placeholder="New lead has been created..."
                                  value={action.action_data?.message || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setNewRule(prev => {
                                      const updatedActions = [...prev.actions];
                                      const updatedAction = {...updatedActions[actionIndex]};
                                      
                                      // Ensure action_data is initialized
                                      if (!updatedAction.action_data) updatedAction.action_data = {};
                                      
                                      // Update the field
                                      updatedAction.action_data.message = value;
                                      updatedActions[actionIndex] = updatedAction;
                                      
                                      return {
                                        ...prev,
                                        actions: updatedActions
                                      };
                                    });
                                  }}
                                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                ></textarea>
                              </div>
                            </div>
                          )}
                          
                          {action.action_type === 'round_robin_assign' && (
                            <div className="space-y-3">
                              <div>
                                <label htmlFor={`action_${actionIndex}_group_name`} className="block text-sm font-medium text-gray-700 mb-1">
                                  Assignment Group *
                                </label>
                                <select
                                  id={`action_${actionIndex}_group_name`}
                                  required
                                  value={action.action_data?.group_name || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    console.log(`Setting group_name for action ${actionIndex} to:`, value);
                                    setNewRule(prev => {
                                      const updatedActions = [...prev.actions];
                                      const updatedAction = {...updatedActions[actionIndex]};
                                      
                                      // Ensure action_data is initialized
                                      if (!updatedAction.action_data) updatedAction.action_data = {};
                                      
                                      // Update the field
                                      updatedAction.action_data.group_name = value;
                                      updatedActions[actionIndex] = updatedAction;
                                      
                                      return {
                                        ...prev,
                                        actions: updatedActions
                                      };
                                    });
                                  }}
                                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                >
                                  <option value="">Select an assignment group</option>
                                  {assignmentGroups && assignmentGroups.map(group => (
                                    <option key={group.id} value={group.group_name}>
                                      {group.group_name}
                                    </option>
                                  ))}
                                </select>
                                <p className="mt-1 text-xs text-gray-500">
                                  Select a round-robin assignment group to distribute leads among its members.
                                  If no groups are available, create one in the Assignment Groups tab first.
                                </p>
                              </div>
                            </div>
                          )}
                          
                          {action.action_type === 'send_chat_notification' && (
                            <div className="space-y-4">
                              <div>
                                <label htmlFor={`action_${actionIndex}_message_template`} className="block text-sm font-medium text-gray-700 mb-1">
                                  Message Template *
                                </label>
                                <textarea
                                  id={`action_${actionIndex}_message_template`}
                                  required
                                  value={action.action_data?.message_template || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setNewRule(prev => {
                                      const updatedActions = [...prev.actions];
                                      const updatedAction = {...updatedActions[actionIndex]};
                                      
                                      // Ensure action_data is initialized
                                      if (!updatedAction.action_data) updatedAction.action_data = {};
                                      
                                      // Update the field
                                      updatedAction.action_data.message_template = value;
                                      updatedActions[actionIndex] = updatedAction;
                                      
                                      return {
                                        ...prev,
                                        actions: updatedActions
                                      };
                                    });
                                  }}
                                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                  rows={3}
                                  placeholder={"Enter your message template with variables like {{user}}, {{value}}, {{owner}}, etc."}
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                  You can use the following variables:<br />
                                  <span className="font-mono">{"{{user}}"}</span> - Name of the user who triggered the event<br />
                                  <span className="font-mono">{"{{owner}}"}</span> - Name of the deal owner<br />
                                  <span className="font-mono">{"{{value}}"}</span> - Deal value with currency formatting<br />
                                  <span className="font-mono">{"{{stage}}"}</span> - Deal stage name<br />
                                  <span className="font-mono">{"{{id}}"}</span> - Deal ID
                                </p>
                              </div>
                              <div>
                                <label htmlFor={`action_${actionIndex}_recipient_id`} className="block text-sm font-medium text-gray-700 mb-1">
                                  Send To Specific User (optional)
                                </label>
                                <select
                                  id={`action_${actionIndex}_recipient_id`}
                                  value={action.action_data?.recipient_id || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setNewRule(prev => {
                                      const updatedActions = [...prev.actions];
                                      const updatedAction = {...updatedActions[actionIndex]};
                                      
                                      // Ensure action_data is initialized
                                      if (!updatedAction.action_data) updatedAction.action_data = {};
                                      
                                      // Update the field
                                      updatedAction.action_data.recipient_id = value;
                                      updatedActions[actionIndex] = updatedAction;
                                      
                                      return {
                                        ...prev,
                                        actions: updatedActions
                                      };
                                    });
                                  }}
                                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                >
                                  <option value="">Team Chat (everyone)</option>
                                  {users.map(user => (
                                    <option key={user.id} value={user.id}>
                                      {user.name}
                                    </option>
                                  ))}
                                </select>
                                <p className="mt-1 text-xs text-gray-500">
                                  Leave empty to send to the team chat. Select a user to send a direct message.
                                </p>
                              </div>
                            </div>
                          )}
                          
                          {action.action_type === 'create_deal' && (
                            <div className="space-y-3">
                              {/* Deal Title */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Deal Title *
                                </label>
                                
                                {/* Use Lead Email Checkbox */}
                                <div className="mb-2">
                                  <label className="inline-flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={action.action_data?.use_lead_email || false}
                                      onChange={(e) => {
                                        setNewRule(prev => ({
                                          ...prev,
                                          actions: prev.actions.map((a, i) => 
                                            i === actionIndex 
                                              ? { 
                                                  ...a, 
                                                  action_data: { 
                                                    ...a.action_data, 
                                                    use_lead_email: e.target.checked,
                                                    deal_title: e.target.checked ? '' : a.action_data?.deal_title || ''
                                                  } 
                                                }
                                              : a
                                          )
                                        }));
                                      }}
                                      className="rounded border-gray-300 text-green-600 shadow-sm focus:border-green-300 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                                    />
                                    <span className="ml-2 text-sm text-gray-600">Use lead email as deal title</span>
                                  </label>
                                </div>

                                {/* Deal Title Input (only show if not using lead email) */}
                                {!action.action_data?.use_lead_email && (
                                  <input
                                    type="text"
                                    id={`action_${actionIndex}_deal_title`}
                                    placeholder="e.g., High-intent prospect from Mixpanel"
                                    value={action.action_data?.deal_title || ''}
                                    onChange={(e) => {
                                      setNewRule(prev => ({
                                        ...prev,
                                        actions: prev.actions.map((a, i) => 
                                          i === actionIndex 
                                            ? { ...a, action_data: { ...a.action_data, deal_title: e.target.value } }
                                            : a
                                        )
                                      }));
                                    }}
                                    className="shadow-sm focus:ring-green-500 focus:border-green-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                  />
                                )}
                                
                                {action.action_data?.use_lead_email && (
                                  <p className="text-sm text-gray-500 italic">Deal title will be set to the lead's email address</p>
                                )}
                              </div>

                              {/* Deal Value */}
                              <div>
                                <label htmlFor={`action_${actionIndex}_deal_value`} className="block text-sm font-medium text-gray-700 mb-1">
                                  Deal Value
                                </label>
                                <input
                                  type="number"
                                  id={`action_${actionIndex}_deal_value`}
                                  placeholder="0"
                                  value={action.action_data?.deal_value || ''}
                                  onChange={(e) => {
                                    setNewRule(prev => ({
                                      ...prev,
                                      actions: prev.actions.map((a, i) => 
                                        i === actionIndex 
                                          ? { ...a, action_data: { ...a.action_data, deal_value: e.target.value } }
                                          : a
                                      )
                                    }));
                                  }}
                                  className="shadow-sm focus:ring-green-500 focus:border-green-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                />
                              </div>

                              {/* Pipeline Stage */}
                              <div>
                                <label htmlFor={`action_${actionIndex}_pipeline_stage`} className="block text-sm font-medium text-gray-700 mb-1">
                                  Pipeline Stage *
                                </label>
                                <select
                                  id={`action_${actionIndex}_pipeline_stage`}
                                  value={action.action_data?.pipeline_stage || ''}
                                  onChange={(e) => {
                                    setNewRule(prev => ({
                                      ...prev,
                                      actions: prev.actions.map((a, i) => 
                                        i === actionIndex 
                                          ? { ...a, action_data: { ...a.action_data, pipeline_stage: e.target.value } }
                                          : a
                                      )
                                    }));
                                  }}
                                  className="shadow-sm focus:ring-green-500 focus:border-green-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                >
                                  <option value="">Select stage...</option>
                                  {pipelineStages.map(stage => (
                                    <option key={stage.id} value={stage.id}>
                                      {stage.name}
                                    </option>
                                  ))}
                                </select>
                              </div>

                            </div>
                          )}
                          
                          {action.action_type === 'update_deal' && (
                            <div className="space-y-3">
                              {/* Deal Stage */}
                              <div>
                                <label htmlFor={`action_${actionIndex}_deal_stage`} className="block text-sm font-medium text-gray-700 mb-1">
                                  Move to Stage *
                                </label>
                                <select
                                  id={`action_${actionIndex}_deal_stage`}
                                  value={action.action_data?.deal_stage || ''}
                                  onChange={(e) => {
                                    setNewRule(prev => ({
                                      ...prev,
                                      actions: prev.actions.map((a, i) => 
                                        i === actionIndex 
                                          ? { ...a, action_data: { ...a.action_data, deal_stage: e.target.value } }
                                          : a
                                      )
                                    }));
                                  }}
                                  className="shadow-sm focus:ring-green-500 focus:border-green-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                >
                                  <option value="">Select a pipeline stage</option>
                                  {pipelineStages.map(stage => (
                                    <option key={stage.id} value={stage.id}>
                                      {stage.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          )}
                          
                          {action.action_type === 'send_email' && (
                            <div>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label htmlFor={`action_${actionIndex}_template_id`} className="block text-sm font-medium text-gray-700 mb-1">
                                    Email Template *
                                  </label>
                                  <select
                                    id={`action_${actionIndex}_template_id`}
                                    required
                                    value={action.action_data?.template_id || ''}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setNewRule(prev => {
                                        const updatedActions = [...prev.actions];
                                        const updatedAction = {...updatedActions[actionIndex]};
                                        
                                        // Ensure action_data is initialized
                                        if (!updatedAction.action_data) updatedAction.action_data = {};
                                        
                                        // Update the field
                                        updatedAction.action_data.template_id = value;
                                        updatedActions[actionIndex] = updatedAction;
                                        
                                        return {
                                          ...prev,
                                          actions: updatedActions
                                        };
                                      });
                                    }}
                                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                  >
                                    <option value="">Select an email template</option>
                                    {emailTemplates.map(template => (
                                      <option key={template.id} value={template.id}>
                                        {template.name} {template.is_default ? '(Default)' : ''}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                
                                <div>
                                  <label htmlFor={`action_${actionIndex}_smtp_profile_id`} className="block text-sm font-medium text-gray-700 mb-1">
                                    SMTP Profile
                                  </label>
                                  <select
                                    id={`action_${actionIndex}_smtp_profile_id`}
                                    value={action.action_data?.smtp_profile_id || ''}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setNewRule(prev => {
                                        const updatedActions = [...prev.actions];
                                        const updatedAction = {...updatedActions[actionIndex]};
                                        
                                        // Ensure action_data is initialized
                                        if (!updatedAction.action_data) updatedAction.action_data = {};
                                        
                                        // Update the field
                                        updatedAction.action_data.smtp_profile_id = value || null;
                                        updatedActions[actionIndex] = updatedAction;
                                        
                                        return {
                                          ...prev,
                                          actions: updatedActions
                                        };
                                      });
                                    }}
                                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                  >
                                    <option value="">Use System Default Profile</option>
                                    {Array.isArray(smtpProfiles) && smtpProfiles.map(profile => (
                                      <option key={profile.id} value={profile.id}>
                                        {profile.name} {profile.is_default ? '(Default)' : ''}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>

                              <div className="mt-3 flex flex-col lg:flex-row lg:items-center">
                                <div className="text-sm font-medium text-gray-700 flex items-center mb-1 lg:mb-0 lg:mr-3">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Available Variables:
                                </div>
                                <div className="text-xs text-gray-600 flex flex-wrap gap-x-3 gap-y-1">
                                  <code className="bg-gray-100 px-1 rounded">{'{{name}}'}</code>
                                  <code className="bg-gray-100 px-1 rounded">{'{{email}}'}</code>
                                  {newRule.trigger_event === 'deal_updated' && (
                                    <>
                                      <code className="bg-gray-100 px-1 rounded">{'{{course}}'}</code>
                                      <code className="bg-gray-100 px-1 rounded">{'{{amount}}'}</code>
                                      <code className="bg-gray-100 px-1 rounded">{'{{stage}}'}</code>
                                      <code className="bg-gray-100 px-1 rounded">{'{{currency}}'}</code>
                                      <code className="bg-gray-100 px-1 rounded">{'{{student_name}}'}</code>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                                </div>
                              ))}
                              
                              {/* Activation Toggle */}
                      <div className="mb-6 bg-white p-5 rounded-lg shadow-sm border border-gray-100 flex items-center">
                        <div className="flex items-center h-5">
                          <input
                            type="checkbox"
                            name="is_active"
                            id="is_active"
                            checked={newRule.is_active}
                            onChange={handleNewRuleChange}
                            className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded-md"
                          />
                        </div>
                        <div className="ml-3">
                          <label htmlFor="is_active" className="font-medium text-gray-800 text-base">Enable this rule</label>
                          <p className="text-gray-500 text-sm">When enabled, this rule will be triggered automatically when conditions are met.</p>
                        </div>
                      </div>
                      
                      {/* Buttons */}
                      <div className="flex justify-end space-x-4">
                        <button
                          type="button"
                          onClick={handleAttemptClose} // Use handleAttemptClose for Cancel button
                          className="inline-flex items-center px-5 py-2.5 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="inline-flex items-center px-5 py-2.5 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          {isEditMode ? 'Update Rule' : 'Create Rule'}
                        </button>
                      </div>
                    </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDeleteRule}
        message="Are you sure you want to delete this automation rule? This action cannot be undone."
      />

      {/* Close Confirmation Modal */}
      <ConfirmationModal
        isOpen={showCloseConfirm}
        onClose={() => setShowCloseConfirm(false)}
        onConfirm={closeModalCleanup}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to discard them and close?"
        confirmButtonText="Discard Changes"
        cancelButtonText="Keep Editing"
        confirmButtonClass="bg-red-600 hover:bg-red-700 focus:ring-red-500" // Make discard button red
      />
    </div>
  );
};

export default AutomationSettings;
